import axios from 'axios'
import { getValidAccessToken, isHttpAuthError } from './tokenManager'

export interface IError {
  message?: string
}

export interface Meta {
  page: number
  limit: number
  total: number
  pages: number
}

export interface ApiResponse<T> {
  success: boolean
  message: string
  error: null | IError
  data: T
  meta?: Meta
}

export const fetcher = (url: string) => axios.get(url).then((res) => res.data)

/**
 * @deprecated Use smartAuthenticatedFetcher instead
 * Legacy fetcher kept for backward compatibility
 */
export const authenticatedFetcher = (url: string, authToken: string) =>
  axios
    .get(url, { headers: { Authorization: authToken } })
    .then((res) => res.data)

/**
 * Smart authenticated fetcher that automatically handles token refresh
 * - Gets fresh token automatically
 * - Retries once on 401/403 errors after refreshing token
 * - Throws on final auth failure (triggers global error handler)
 */
export const smartAuthenticatedFetcher = async (url: string) => {
  try {
    // Get valid token (will refresh if needed)
    const token = await getValidAccessToken()
    
    // Make request with valid token
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
    
    return response.data
  } catch (error) {
    // If it's an auth error (401/403), try to refresh and retry once
    if (isHttpAuthError(error)) {
      try {
        // Force token refresh by getting valid token again
        const newToken = await getValidAccessToken()
        
        // Retry request with new token
        const retryResponse = await axios.get(url, {
          headers: { Authorization: `Bearer ${newToken}` },
        })
        
        return retryResponse.data
      } catch (retryError) {
        // If retry also fails, throw the error
        // This will trigger the global auth error handler
        console.error('Token refresh retry failed:', retryError)
        throw retryError
      }
    }
    
    // For non-auth errors, just throw
    throw error
  }
}
